import axios from "axios";

const TOKEN_KEY = "datumcraft.auth.token";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export const api = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearAuthToken();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  },
);

export type AskResponse = {
  conversationId: string;
  question: string;
  standaloneQuestion: string;
  sql?: string;
  rejected: boolean;
  violations?: string[];
  result?: { columnNames: string[]; rows: unknown[][]; rowCount: number };
  executionError?: string;
  narrative?: {
    summary: string;
    keyFindings: string[];
    followUpQuestions: string[];
    proposedActions: Array<{
      actionType: string;
      summary: string;
      rationale?: string;
      targetIds: string[];
      parameters: Record<string, unknown>;
    }>;
  };
  clarification?: string;
};

export type SessionSummary = {
  id: string;
  sessionId: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
};

export type SessionMessage = {
  id: number;
  role: string;
  content: string;
  createdAt: string;
};

export type ConfigResponse = {
  llmProvider: string;
  semanticLayerVersion: string;
  /**
   * Cognos SDK fields are env-derived (datumcraft.cognos.sdk.*); null
   * when the SDK module is disabled. Password is intentionally NOT
   * exposed by the backend.
   */
  cognosDispatcherUrl: string | null;
  cognosNamespace: string | null;
  cognosUsername: string | null;
  /** Operator-set deployment label; null when never set. */
  instanceName: string | null;
  /**
   * Operator-set default for the cognos.recycle.list tool's
   * recoverableOnly parameter. Resolved server-side per design §6.2:
   * DB setting > env var > true.
   */
  recycleDefaultRecoverableOnly: boolean;
  featureFlags: Record<string, boolean>;
};

/** Admin-only — clears the setting when name is empty / null. */
export async function setInstanceName(name: string | null): Promise<{ instanceName: string | null }> {
  const { data } = await api.put<{ instanceName: string | null }>(
    "/config/instance-name",
    { name: name && name.trim() ? name.trim() : null },
  );
  return data;
}

/** Admin-only — flip the global default for the recycle-bin list. */
export async function setRecycleDefaultRecoverableOnly(
  recoverableOnly: boolean,
): Promise<{ recoverableOnly: boolean }> {
  const { data } = await api.put<{ recoverableOnly: boolean }>(
    "/config/recycle-default-recoverable-only",
    { recoverableOnly },
  );
  return data;
}

export type LicenseStatus = {
  ok: boolean;
  failureReason?: string | null;
  failureMessage?: string | null;
  customerName?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  daysRemaining: number;
  fingerprint: string;
};

// ----- Wave-12: saved questions, anomaly rules, scheduled digests -----

export type SavedQuestion = {
  id: string;
  userId?: string | null;
  name: string;
  question: string;
  pinned: boolean;
  sourceConversationId?: string | null;
  sourceMessageId?: number | null;
  createdAt?: string | null;
  lastRunAt?: string | null;
  runCount: number;
};

export async function listSavedQuestions(): Promise<SavedQuestion[]> {
  const { data } = await api.get<SavedQuestion[]>("/saved-questions");
  return data;
}

export async function createSavedQuestion(payload: {
  name: string;
  question: string;
  pinned?: boolean;
  sourceConversationId?: string;
  sourceMessageId?: number;
}): Promise<SavedQuestion> {
  const { data } = await api.post<SavedQuestion>("/saved-questions", payload);
  return data;
}

export async function updateSavedQuestion(
  id: string,
  patch: Partial<Pick<SavedQuestion, "name" | "question" | "pinned">>,
): Promise<SavedQuestion> {
  const { data } = await api.put<SavedQuestion>(`/saved-questions/${id}`, patch);
  return data;
}

export async function deleteSavedQuestion(id: string): Promise<void> {
  await api.delete(`/saved-questions/${id}`);
}

export type AnomalyRule = {
  id: string;
  name: string;
  description?: string | null;
  metricSql: string;
  operator: ">" | ">=" | "<" | "<=" | "==" | "!=";
  threshold: number;
  alertAction: "LOG" | "WEBHOOK" | "EMAIL";
  alertTarget?: string | null;
  active: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastEvaluatedAt?: string | null;
  lastStatus?: "OK" | "BREACHED" | "ERROR" | null;
};

export type AnomalyAlert = {
  id: number;
  ruleId: string;
  triggeredAt: string;
  observedValue: number;
  thresholdAtTrigger: number;
  operatorAtTrigger: string;
  deliveryStatus: "LOGGED" | "DELIVERED" | "FAILED" | "SKIPPED";
  deliveryDetail?: string | null;
};

export async function listRules(): Promise<AnomalyRule[]> {
  const { data } = await api.get<AnomalyRule[]>("/rules");
  return data;
}

export async function createRule(payload: Omit<AnomalyRule, "id" | "createdAt" | "updatedAt" | "lastEvaluatedAt" | "lastStatus">): Promise<AnomalyRule> {
  const { data } = await api.post<AnomalyRule>("/rules", payload);
  return data;
}

export async function updateRule(id: string, patch: Partial<AnomalyRule>): Promise<AnomalyRule> {
  const { data } = await api.put<AnomalyRule>(`/rules/${id}`, patch);
  return data;
}

export async function deleteRule(id: string): Promise<void> {
  await api.delete(`/rules/${id}`);
}

export async function evaluateRule(id: string): Promise<AnomalyAlert | null> {
  const r = await api.post(`/rules/${id}/evaluate`);
  return r.status === 204 ? null : (r.data as AnomalyAlert);
}

export async function listRecentAlerts(): Promise<AnomalyAlert[]> {
  const { data } = await api.get<AnomalyAlert[]>("/rules/alerts/recent");
  return data;
}

export type DigestSchedule = {
  id: string;
  name: string;
  cronExpression: string;
  savedQuestionId?: string | null;
  inlineQuestion?: string | null;
  deliveryAction: "LOG" | "WEBHOOK" | "EMAIL";
  deliveryTarget?: string | null;
  active: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastRunAt?: string | null;
  lastStatus?: "OK" | "FAILED" | null;
};

export type DigestRun = {
  id: number;
  scheduleId: string;
  startedAt: string;
  completedAt?: string | null;
  status: "RUNNING" | "SUCCEEDED" | "FAILED";
  rowCount?: number | null;
  deliveryStatus?: "LOGGED" | "DELIVERED" | "FAILED" | "SKIPPED" | null;
  deliveryDetail?: string | null;
  errorMessage?: string | null;
};

export async function listDigests(): Promise<DigestSchedule[]> {
  const { data } = await api.get<DigestSchedule[]>("/digests");
  return data;
}

export async function createDigest(payload: Omit<DigestSchedule, "id" | "createdAt" | "updatedAt" | "lastRunAt" | "lastStatus">): Promise<DigestSchedule> {
  const { data } = await api.post<DigestSchedule>("/digests", payload);
  return data;
}

export async function updateDigest(id: string, patch: Partial<DigestSchedule>): Promise<DigestSchedule> {
  const { data } = await api.put<DigestSchedule>(`/digests/${id}`, patch);
  return data;
}

export async function deleteDigest(id: string): Promise<void> {
  await api.delete(`/digests/${id}`);
}

export async function runDigestNow(id: string): Promise<DigestRun> {
  const { data } = await api.post<DigestRun>(`/digests/${id}/run`);
  return data;
}

export async function listRecentDigestRuns(): Promise<DigestRun[]> {
  const { data } = await api.get<DigestRun[]>("/digests/runs/recent");
  return data;
}
